import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './rootReducer';
import {
  AdvancedProperties,
  AdvancedPropertiesKeys,
  AstronautEventProperties,
  GeneralProperties,
  GeneralPropertiesKeys,
} from '../components/AstronautDataExplorer/AstronautFilterMenu/types';
import { Nullable } from '../common/types';

type MenuOption<T extends AdvancedPropertiesKeys | GeneralPropertiesKeys> = {
  color: string;
  name: T;
  iconClass: string;
};

interface EventMenuLevelData {
  generalEvents: Array<MenuOption<GeneralPropertiesKeys>>;
  advanced: Array<MenuOption<AdvancedPropertiesKeys>>;
}

export interface EventLevelState {
  eventLevels: Nullable<GeneralProperties>;
  advancedLevels: Nullable<AdvancedProperties>;
  errorMessage: string;
}

export const initialState: EventLevelState = {
  eventLevels: null,
  advancedLevels: null,
  errorMessage: '',
};

const eventsLevelMenuOptionsSlice = createSlice({
  name: 'eventsLevelMenuOptions',
  initialState,
  reducers: {
    getEventMenuLevelsSuccess: (
      state,
      action: PayloadAction<{ eventMenuLevelData: EventMenuLevelData }>,
    ) => {
      const eventsLevelMenuOptions: GeneralProperties = {
        EMERGENCY: {} as AstronautEventProperties,
        CRITICAL: {} as AstronautEventProperties,
        ERROR: {} as AstronautEventProperties,
        WARNING: {} as AstronautEventProperties,
      };
      const advancedLevels: AdvancedProperties = {
        DEBUG: {} as AstronautEventProperties,
      };

      action.payload.eventMenuLevelData.generalEvents.forEach(
        (currentEventOption) => {
          const currentEventLevel = currentEventOption.name;
          eventsLevelMenuOptions[currentEventLevel] = {
            itemCSSClass: `bb-events-chart-${currentEventLevel}`,
            isSelected: true,
            color: currentEventOption.color,
            iconClass: currentEventOption.iconClass,
          };
        },
      );

      action.payload.eventMenuLevelData.advanced.forEach(
        (currentEventOption) => {
          const currentEventLevel = currentEventOption.name;
          advancedLevels[currentEventLevel] = {
            itemCSSClass: `bb-events-chart-${currentEventLevel}`,
            isSelected: true,
            color: currentEventOption.color,
            iconClass: currentEventOption.iconClass,
          };
        },
      );

      state.eventLevels = eventsLevelMenuOptions;
      state.advancedLevels = advancedLevels;
      state.errorMessage = '';
    },
    getEventMenuLevelsError: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
      state.eventLevels = null;
      state.advancedLevels = null;
    },
  },
});
export const getEventMenuLevels = createAction('GET_EVENT_MENU_LEVELS_REQUEST');
// Selectors
export const selectEventLevels = (state: RootState) =>
  state.eventsLevelMenuOptions.eventLevels;
export const selectAdvancedLevels = (state: RootState) =>
  state.eventsLevelMenuOptions.advancedLevels;
export const selectErrorMessage = (state: RootState) =>
  state.eventsLevelMenuOptions.errorMessage;

export const { getEventMenuLevelsSuccess, getEventMenuLevelsError } =
  eventsLevelMenuOptionsSlice.actions;

export default eventsLevelMenuOptionsSlice.reducer;
